import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import type { StartupSettingsInitialData } from "../StartupSettingsForm/StartupSettingsForm";
import type { CompanyAddress, InvestorContact, UpdateStartupData } from "@/types/startup.types";

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "E-commerce",
  "Education",
  "Manufacturing",
  "Real Estate",
  "Food & Beverage",
  "Media & Entertainment",
  "Transportation",
  "Energy",
  "Other",
];

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "CNY"];

interface StartupDetailsValues {
  name: string;
  legalName?: string;
  tagline?: string;
  website?: string;
  description?: string;
  initialFunding: number;
  industry?: string;
  currency: string;
  phone?: string;
  addressStreet?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  investorContactName: string;
  investorContactEmail: string;
  investorContactPhone?: string;
  investorContactCalendarUrl?: string;
  investorContactMessage?: string;
}

export interface StartupDetailsSettingsProps {
  initialData?: StartupSettingsInitialData;
  onSubmit: (data: UpdateStartupData) => Promise<void>;
  isLoading?: boolean;
}

export const StartupDetailsSettings: React.FC<StartupDetailsSettingsProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
}) => {
  const defaultValues = useMemo((): StartupDetailsValues => {
    const contact = initialData?.investorContact;
    const address = initialData?.address;
    return {
      name: initialData?.name ?? "",
      legalName: initialData?.legalName ?? "",
      tagline: initialData?.tagline ?? "",
      website: initialData?.website ?? "",
      description: initialData?.description ?? "",
      initialFunding: initialData?.initialFunding ?? 0,
      industry: initialData?.industry ?? "",
      currency: initialData?.currency ?? "USD",
      phone: initialData?.phone ?? "",
      addressStreet: address?.street ?? "",
      addressCity: address?.city ?? "",
      addressState: address?.state ?? "",
      addressPostalCode: address?.postalCode ?? "",
      addressCountry: address?.country ?? "",
      investorContactName: contact?.name ?? "",
      investorContactEmail: contact?.email ?? "",
      investorContactPhone: contact?.phone ?? "",
      investorContactCalendarUrl: contact?.calendarUrl ?? "",
      investorContactMessage: contact?.message ?? "",
    };
  }, [initialData]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<StartupDetailsValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <div className="space-y-6">
      <SettingsSectionCard
        title="Company profile"
        description="Identity and contact details shown in investor views and business plan generation."
      >
        <form
          onSubmit={handleSubmit(async (data) => {
            const address: CompanyAddress = {
              street: data.addressStreet || undefined,
              city: data.addressCity || undefined,
              state: data.addressState || undefined,
              postalCode: data.addressPostalCode || undefined,
              country: data.addressCountry || undefined,
            };

            const investorContact: InvestorContact = {
              name: data.investorContactName.trim(),
              email: data.investorContactEmail.trim(),
              phone: data.investorContactPhone?.trim() || undefined,
              calendarUrl: data.investorContactCalendarUrl?.trim() || undefined,
              message: data.investorContactMessage?.trim() || undefined,
            };

            await onSubmit({
              name: data.name,
              legalName: data.legalName || undefined,
              tagline: data.tagline || undefined,
              website: data.website || undefined,
              description: data.description,
              initialFunding: Number(data.initialFunding),
              industry: data.industry,
              currency: data.currency,
              phone: data.phone || undefined,
              address,
              investorContact,
            });
            reset(data);
          })}
          className="space-y-4"
        >
          <FormField label="Startup name" error={errors.name?.message}>
            <Input
              {...register("name", { required: "Startup name is required" })}
              placeholder="e.g., Acme Labs"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Legal name (optional)">
              <Input {...register("legalName")} placeholder="Acme Labs, Inc." />
            </FormField>
            <FormField label="Tagline (optional)">
              <Input {...register("tagline")} placeholder="One-line pitch" />
            </FormField>
          </div>

          <FormField label="Website (optional)">
            <Input {...register("website")} placeholder="https://example.com" />
          </FormField>

          <FormField label="Description">
            <textarea
              {...register("description")}
              placeholder="Brief description of your startup..."
              className="sh-select min-h-[96px]"
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Industry">
              <select {...register("industry")} className="sh-select">
                <option value="">Select industry</option>
                {INDUSTRIES.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Currency" error={errors.currency?.message}>
              <select
                {...register("currency", { required: "Currency is required" })}
                className="sh-select"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <FormField label="Initial funding ($)" error={errors.initialFunding?.message}>
            <Input
              type="number"
              step="0.01"
              {...register("initialFunding", {
                required: "Initial funding is required",
                min: { value: 0, message: "Amount must be positive" },
                valueAsNumber: true,
              })}
            />
          </FormField>

          <FormField label="Phone (optional)">
            <Input {...register("phone")} placeholder="+1 (555) 123-4567" />
          </FormField>

          <div className="rounded-[14px] border border-white/10 p-4 space-y-3">
            <div className="text-sm font-semibold text-white">Address (optional)</div>
            <FormField label="Street">
              <Input {...register("addressStreet")} />
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="City">
                <Input {...register("addressCity")} />
              </FormField>
              <FormField label="State / region">
                <Input {...register("addressState")} />
              </FormField>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Postal code">
                <Input {...register("addressPostalCode")} />
              </FormField>
              <FormField label="Country">
                <Input {...register("addressCountry")} />
              </FormField>
            </div>
          </div>

          <div className="rounded-[14px] border border-white/10 p-4 space-y-3">
            <div className="text-sm font-semibold text-white">Investor contact</div>
            <p className="text-xs text-white/55">
              Shown when a share link expires or is revoked so viewers can reach you.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Contact name"
                error={errors.investorContactName?.message}
              >
                <Input
                  {...register("investorContactName", {
                    required: "Contact name is required",
                  })}
                />
              </FormField>
              <FormField
                label="Contact email"
                error={errors.investorContactEmail?.message}
              >
                <Input
                  type="email"
                  {...register("investorContactEmail", {
                    required: "Contact email is required",
                  })}
                />
              </FormField>
            </div>
            <FormField label="Contact phone (optional)">
              <Input {...register("investorContactPhone")} />
            </FormField>
            <FormField label="Calendar URL (optional)">
              <Input {...register("investorContactCalendarUrl")} placeholder="https://calendly.com/..." />
            </FormField>
            <FormField label="Message (optional)">
              <textarea
                {...register("investorContactMessage")}
                className="sh-select min-h-[72px]"
                placeholder="Happy to walk through the model — reach out anytime."
              />
            </FormField>
          </div>

          <div className="flex justify-end border-t border-white/10 pt-4">
            <Button type="submit" variant="primary" size="md" disabled={isLoading || !isDirty}>
              {isLoading ? "Saving..." : "Save profile"}
            </Button>
          </div>
        </form>
      </SettingsSectionCard>
    </div>
  );
};
